/**
 * Sysadmin Fine-Tuning → Evaluation (Phase 5a infrastructure).
 *
 * Lists multi-gate evaluation runs and exposes the admin approve / reject
 * gate. Phase 5b's evaluators (automated RAGAS scoring, safety adversarial
 * harness, SME review) will drive the intermediate state transitions; the
 * UI here renders the state machine + final approval.
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  ClipboardCheck,
  Plus,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Loader2,
  Inbox,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { StatTile } from '@/components/ui/stat-tile';
import { StatusPill } from '@/components/ui/status-pill';
import { PageError } from '@/components/ui/page-error';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useFtEvaluationRuns,
  useCreateFtEvalRun,
  useApproveFtEvalRun,
  useRejectFtEvalRun,
  type FtEvalRunRow,
  type FtEvalStatus,
} from '@/lib/use-sysadmin-fine-tuning';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/fine-tuning/evaluation')({
  component: FtEvaluationPage,
});

function FtEvaluationPage() {
  const { data, isLoading, error, refetch, isFetching } = useFtEvaluationRuns();
  const [creating, setCreating] = useState(false);
  const [decideRun, setDecideRun] = useState<{
    row: FtEvalRunRow;
    mode: 'approve' | 'reject';
  } | null>(null);

  const items = data?.items ?? [];

  const counts = {
    total: items.length,
    inFlight: items.filter((r) =>
      [
        'queued',
        'automated_running',
        'safety_running',
        'sme_pending',
        'admin_pending',
      ].includes(r.status)
    ).length,
    approved: items.filter((r) => r.status === 'approved').length,
    rejected: items.filter((r) =>
      ['rejected', 'sme_rejected', 'failed', 'safety_failed', 'automated_failed'].includes(r.status)
    ).length,
  };

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <PageError
          title="Failed to load evaluation runs"
          message={error.message}
          onRetry={() => refetch()}
          retrying={isFetching}
        />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <ClipboardCheck className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Evaluation Runs
            </h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge variant="outline" className="text-xs font-mono">
                Phase 5a
              </Badge>
              <span className="text-xs text-muted-foreground">
                Multi-gate state machine. Phase 5b plugs in automated +
                safety + SME evaluators; admin approval is wired today.
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw
              className={cn('h-3.5 w-3.5 mr-1.5', isFetching && 'animate-spin')}
            />
            Refresh
          </Button>
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            New evaluation
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Total" value={String(counts.total)} icon={ClipboardCheck} tone="default" />
        <StatTile label="In flight" value={String(counts.inFlight)} icon={Loader2} tone={counts.inFlight > 0 ? 'default' : 'muted'} />
        <StatTile label="Approved" value={String(counts.approved)} icon={CheckCircle2} tone={counts.approved > 0 ? 'success' : 'muted'} />
        <StatTile label="Rejected/Failed" value={String(counts.rejected)} icon={XCircle} tone={counts.rejected > 0 ? 'destructive' : 'muted'} />
      </div>

      {isLoading && items.length === 0 ? (
        <div className="py-10 text-center text-muted-foreground text-sm animate-pulse">
          Loading evaluation runs…
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={ClipboardCheck}
          title="No evaluation runs yet"
          body="Create a run from an adapter whose trainingStatus is complete. Phase 5a wires the state machine end-to-end so an admin can approve / reject manually; Phase 5b plugs in the automated, safety, and SME evaluators."
        />
      ) : (
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Adapter</th>
                <th className="px-3 py-2 font-medium">Tenant</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium text-right">Aggregate</th>
                <th className="px-3 py-2 font-medium text-right">Pairs</th>
                <th className="px-3 py-2 font-medium">Started</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <RunRow
                  key={r.id}
                  row={r}
                  onApprove={() => setDecideRun({ row: r, mode: 'approve' })}
                  onReject={() => setDecideRun({ row: r, mode: 'reject' })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateRunDialog
          open={creating}
          onOpenChange={setCreating}
          onSuccess={() => refetch()}
        />
      )}

      {decideRun && (
        <DecisionDialog
          run={decideRun.row}
          mode={decideRun.mode}
          onClose={() => setDecideRun(null)}
          onSuccess={() => {
            refetch();
            setDecideRun(null);
          }}
        />
      )}
    </div>
  );
}

function RunRow({
  row,
  onApprove,
  onReject,
}: {
  row: FtEvalRunRow;
  onApprove: () => void;
  onReject: () => void;
}) {
  const canDecide = row.status === 'admin_pending';
  const [expanded, setExpanded] = useState(false);
  const hasGateDetail =
    row.gateResults &&
    typeof row.gateResults === 'object' &&
    Object.keys(row.gateResults).length > 0;
  return (
    <>
      <tr className="border-t hover:bg-muted/30 transition-colors">
        <td className="px-3 py-2.5 font-mono text-[11px] text-muted-foreground">
          <button
            onClick={() => hasGateDetail && setExpanded((v) => !v)}
            className={cn(
              'inline-flex items-center gap-1',
              hasGateDetail && 'hover:text-foreground'
            )}
            title={hasGateDetail ? 'Toggle gate detail' : 'No gate detail yet'}
            disabled={!hasGateDetail}
          >
            <span className="text-muted-foreground">{expanded ? '▾' : '▸'}</span>
            {row.adapterId.slice(0, 8)}
          </button>
        </td>
        <td className="px-3 py-2.5 font-mono text-[11px] text-muted-foreground">
          {row.tenantId.slice(0, 8)}
        </td>
        <td className="px-3 py-2.5">
          <EvalStatusPill status={row.status} />
          {row.status === 'rejected' && row.rejectionReason && (
            <div className="text-[10px] text-destructive mt-1 max-w-xs truncate" title={row.rejectionReason}>
              {row.rejectionReason}
            </div>
          )}
        </td>
        <td className="px-3 py-2.5 text-right font-mono text-xs">
          {row.aggregateScore != null ? row.aggregateScore.toFixed(3) : '—'}
        </td>
        <td className="px-3 py-2.5 text-right font-mono text-xs">
          {row.pairCount}
        </td>
        <td className="px-3 py-2.5 text-xs">
          {row.startedAt
            ? new Date(row.startedAt).toLocaleString()
            : <span className="text-muted-foreground">—</span>}
        </td>
        <td className="px-3 py-2.5 text-right">
          <div className="inline-flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={onApprove}
              disabled={!canDecide}
              title={canDecide ? 'Approve' : 'Only available in admin_pending state'}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onReject}
              disabled={!canDecide}
              title={canDecide ? 'Reject' : 'Only available in admin_pending state'}
            >
              <XCircle className="h-3.5 w-3.5" />
            </Button>
          </div>
        </td>
      </tr>
      {expanded && hasGateDetail && (
        <tr className="border-t bg-muted/10">
          <td colSpan={7} className="px-4 py-3">
            <GateDetailPanel results={row.gateResults as Record<string, unknown>} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Phase 5b — gate-by-gate breakdown rendered below an expanded run row.
 * Reads the canonical `gateResults` jsonb shape written by the
 * orchestrator. Falls back to a raw JSON preview when the shape is
 * unrecognized (forward-compatible — Phase 5c can add more gates
 * without touching this code).
 */
function GateDetailPanel({ results }: { results: Record<string, unknown> }) {
  const gateA = results.gateA as Record<string, unknown> | undefined;
  const gateB = results.gateB as Record<string, unknown> | undefined;
  const gateC = results.gateC as Record<string, unknown> | undefined;
  const adminNotes = results.adminApprovalNotes as string | undefined;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
      <GateColumn
        title="Gate A — Automated"
        accent="cyan"
        rows={gateA ? [
          ['aggregate', formatNum(gateA.aggregateScore)],
          ['regression', formatNum(gateA.regressionDelta)],
          ['pair pass rate', formatNum(gateA.passRate)],
          ['pairs scored', String(gateA.perPairResults ?? '—')],
          ['reason', String(gateA.reason ?? '—')],
        ] : null}
      />
      <GateColumn
        title="Gate B — Safety"
        accent="amber"
        rows={gateB ? [
          ['pass rate', formatNum(gateB.passRate)],
          ['prompts', String(gateB.totalPrompts ?? '—')],
          ['passed', String(gateB.totalPassed ?? '—')],
          ['reason', String(gateB.reason ?? '—')],
        ] : null}
      />
      <GateColumn
        title="Gate C — SME"
        accent="emerald"
        rows={gateC ? [
          ['thumbs up rate', formatNum(gateC.pctThumbsUp)],
          ['total reviews', String(gateC.totalReviews ?? '—')],
          ['SMEs assigned', String(gateC.assignmentCount ?? '—')],
          ['reason', String(gateC.reason ?? '—')],
        ] : null}
      />
      {adminNotes && (
        <div className="col-span-3 mt-1 text-[11px] text-muted-foreground">
          <span className="font-medium">Admin notes:</span> {adminNotes}
        </div>
      )}
    </div>
  );
}

function GateColumn({
  title,
  accent,
  rows,
}: {
  title: string;
  accent: 'cyan' | 'amber' | 'emerald';
  rows: Array<[string, string]> | null;
}) {
  const dotClass = {
    cyan: 'bg-cyan-500',
    amber: 'bg-amber-500',
    emerald: 'bg-emerald-500',
  }[accent];
  return (
    <div className="rounded-md border bg-background p-2.5">
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className={cn('h-2 w-2 rounded-full', dotClass)} />
        <span className="font-medium text-[11px] uppercase tracking-wide text-muted-foreground">
          {title}
        </span>
      </div>
      {rows ? (
        <table className="w-full">
          <tbody>
            {rows.map(([k, v]) => (
              <tr key={k}>
                <td className="text-muted-foreground pr-2 align-top whitespace-nowrap">{k}</td>
                <td className="font-mono text-[11px] break-all">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="text-muted-foreground text-[11px]">not yet run</div>
      )}
    </div>
  );
}

function formatNum(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'number') return v.toFixed(3);
  return String(v);
}

function EvalStatusPill({ status }: { status: FtEvalStatus }) {
  if (status === 'approved') {
    return (
      <StatusPill status="healthy" compact icon={CheckCircle2}>
        Approved
      </StatusPill>
    );
  }
  if (status === 'rejected' || status === 'sme_rejected') {
    return (
      <StatusPill status="destructive" compact icon={XCircle}>
        {status === 'sme_rejected' ? 'SME rejected' : 'Rejected'}
      </StatusPill>
    );
  }
  if (
    status === 'failed' ||
    status === 'automated_failed' ||
    status === 'safety_failed'
  ) {
    return (
      <StatusPill status="destructive" compact icon={AlertTriangle}>
        {status.replace('_', ' ')}
      </StatusPill>
    );
  }
  if (status === 'queued') {
    return (
      <StatusPill status="muted" compact icon={Inbox}>
        Queued
      </StatusPill>
    );
  }
  if (
    status === 'automated_running' ||
    status === 'safety_running'
  ) {
    return (
      <StatusPill status="info" compact icon={Loader2}>
        {status.replace('_', ' ')}
      </StatusPill>
    );
  }
  if (status === 'sme_pending' || status === 'admin_pending') {
    return (
      <StatusPill status="warning" compact>
        {status.replace('_', ' ')}
      </StatusPill>
    );
  }
  return (
    <StatusPill status="muted" compact>
      {status.replace('_', ' ')}
    </StatusPill>
  );
}

function CreateRunDialog({
  open,
  onOpenChange,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSuccess: () => void;
}) {
  const [adapterId, setAdapterId] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const create = useCreateFtEvalRun();

  const valid = /^[0-9a-f-]{36}$/.test(adapterId);

  const handleSubmit = async () => {
    setSubmitError(null);
    try {
      await create.mutateAsync({ adapterId });
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New evaluation run</DialogTitle>
          <DialogDescription>
            Queues an evaluation run against a candidate adapter. The
            run starts in status=queued; Phase 5b's evaluators move it
            forward, and an admin signs off at the admin_pending gate.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              Adapter ID <span className="text-red-500">*</span>
            </Label>
            <Input
              placeholder="00000000-0000-0000-0000-000000000000"
              value={adapterId}
              onChange={(e) => setAdapterId(e.target.value)}
              className="font-mono text-xs"
            />
            {adapterId.length > 0 && !valid && (
              <p className="text-[10px] text-destructive">Must be a valid UUID.</p>
            )}
          </div>
          {submitError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {submitError}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!valid || create.isPending}>
            {create.isPending && (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            )}
            Queue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DecisionDialog({
  run,
  mode,
  onClose,
  onSuccess,
}: {
  run: FtEvalRunRow;
  mode: 'approve' | 'reject';
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [score, setScore] = useState(
    run.aggregateScore != null ? String(run.aggregateScore) : '0.8'
  );
  const [reason, setReason] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const approve = useApproveFtEvalRun();
  const reject = useRejectFtEvalRun();

  const parsedScore = Number.parseFloat(score);
  const scoreValid = Number.isFinite(parsedScore) && parsedScore >= 0 && parsedScore <= 1;
  const canSubmit =
    mode === 'approve'
      ? scoreValid && !approve.isPending
      : reason.trim().length > 0 && !reject.isPending;

  const handleSubmit = async () => {
    setSubmitError(null);
    try {
      if (mode === 'approve') {
        await approve.mutateAsync({
          id: run.id,
          aggregateScore: parsedScore,
        });
      } else {
        await reject.mutateAsync({
          id: run.id,
          reason: reason.trim(),
          aggregateScore: scoreValid ? parsedScore : undefined,
        });
      }
      onSuccess();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === 'approve' ? 'Approve evaluation' : 'Reject evaluation'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'approve'
              ? 'Promotes the adapter to candidate (eligible for blue/green activation). Emits fine_tuning.evaluation.completed + fine_tuning.adapter.evaluated.'
              : 'Marks the run rejected. The adapter does not advance to candidate.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              Aggregate score (0–1){' '}
              {mode === 'approve' && <span className="text-red-500">*</span>}
            </Label>
            <Input
              type="number"
              min={0}
              max={1}
              step={0.001}
              value={score}
              onChange={(e) => setScore(e.target.value)}
              className="font-mono text-xs"
            />
            {!scoreValid && (
              <p className="text-[10px] text-destructive">
                Must be a number between 0 and 1.
              </p>
            )}
          </div>
          {mode === 'reject' && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">
                Reason <span className="text-red-500">*</span>
              </Label>
              <Textarea
                placeholder="e.g. faithfulness regression on safety-critical pairs"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
              />
            </div>
          )}
          {submitError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {submitError}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {(approve.isPending || reject.isPending) && (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            )}
            {mode === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
